import { DBReducerMap } from "@common-reducers/DBServiceThunks";
import { safeDispatch as dispatch, getStateDirectly } from '@common/state/state-provider';
import { ContentMessageServiceActions } from "@extension-context-content/services/contentMessageServiceActions";
import { prepareMessageForWa } from "@wa-communication-layer/proccess-message-app-context";
import { withTimeout } from "@wa-communication-layer/wa-error-tracing";
import { UpdateEventMessageData } from "./types";
import { userMessagesSelectOneObjectByQuery } from "@common-reducers/DBServiceThunks";

export const handleWhatsappInterfaceEvents = async (updateEvent: any, callback: any) => {
  if (updateEvent.path === 'whatsapp-interface') {
    console.log('[Notifier] Handling whatsapp-interface command:', updateEvent.method);
    try {
      let response;

      // Handle different commands
      switch (updateEvent.method) {
        case 'get_contacts':
          response = await ContentMessageServiceActions.getContacts({
            limit: updateEvent.entity?.params?.limit ?? 100,
            offset: updateEvent.entity?.params?.offset ?? 0
          });
          break;
        case 'get_chats':
          response = await ContentMessageServiceActions.getChats({
            limit: updateEvent.entity?.params?.limit ?? 100,
            offset: updateEvent.entity?.params?.offset ?? 0
          });
          break;
        case 'get_messages':
          response = await ContentMessageServiceActions.getMessages({
            chatId: updateEvent.entity?.params?.chatId,
            limit: updateEvent.entity?.params?.limit ?? 100,
            offset: updateEvent.entity?.params?.offset ?? 0,
            cutoffDate: updateEvent.entity?.params?.cutoffDate,
            latestDate: updateEvent.entity?.params?.latestDate
          });

          response = response
            .filter(msg => msg.type !== 'image')
            .map(msg => {
              const cleanedMsg = {};
              Object.entries(msg).forEach(([key, value]) => {
                if (key !== 'id' && value !== undefined) {
                  cleanedMsg[key] = value;
                }
              });
              return cleanedMsg;
            });
          break;


        case "get_wa_messages_ack_by_wa_messages_keys":
          const waMessageKeys = updateEvent.entity?.params?.params;
          response =
            await ContentMessageServiceActions.getWaMessagesAckByWaMessagesKeys(
              waMessageKeys
            );
          break;
        case "get_wa_messages_ack_data_by_wa_messages_keys":
          const waMessageKeysForAckData = updateEvent.entity?.params?.params
          response = await ContentMessageServiceActions.getWaMessagesAckDataByWaMessagesKeys(
            waMessageKeysForAckData
          );
          break;

        case 'search_messages':
          response = await ContentMessageServiceActions.searchMessages(updateEvent.entity?.params);
          break;
        case 'search_contacts':
          response = await ContentMessageServiceActions.searchContacts(updateEvent.entity?.params);
          break;
        case 'get_profile_pic':
          response = await ContentMessageServiceActions.getProfilePic(updateEvent.entity?.params?.contactIds);
          break;
        case 'send_message_from_whatsapp_interface_actions':
          const timeoutMs = 10000; // Set your timeout duration (in ms)

          const preparedWaMessage = await withTimeout(prepareMessageForWa(updateEvent.entity?.params?.message as any), 'prepareMessageForWa', timeoutMs);
          response = await ContentMessageServiceActions.sendMessageViaWhatsapp(preparedWaMessage, updateEvent.entity?.params?.userMessageId);
          break;
        default:
          throw new Error(`Unknown command: ${updateEvent.method}`);
      }

      console.log('[Notifier] Sending response:', response);
      callback({
        success: true,
        data: response
      });
    } catch (error) {
      console.error('[Notifier] Error processing request:', error);
      callback({
        success: false,
        error: error.message || 'Failed to process request'
      });
    }
  }
  return null;
};

// Handle insertOnlyIfMissing requests from backend
export const handleInsertOnlyIfMissingRequest = async (updateEvent: any, callback: any) => {
  if (updateEvent.path === 'insertOnlyIfMissing') {
    console.log('[Notifier] Handling insertOnlyIfMissing request');
    try {
      const { message, checkStore } = updateEvent.entity || {};
      
      if (!message) {
        throw new Error('Message is required');
      }

      const state = getStateDirectly();
      
      // Check if message already exists in Redux state
      const existingMessage = userMessagesSelectOneObjectByQuery(
        state.DBUserMessageReducer, 
        { _id: message._id }
      );

      let messageExists = !!existingMessage;
      let whatsappState = 'UNKNOWN';

      // If message doesn't exist in Redux, add it
      if (!existingMessage) {
        console.log('[Notifier] Message not found in Redux state, adding to state');
        const dbReducerSlice = DBReducerMap.get('user-messages');
        if (dbReducerSlice) {
          dispatch(dbReducerSlice.slice.actions.addOne(message));
        }
      } else {
        console.log('[Notifier] Message already exists in Redux state');
      }

      // Get current WhatsApp app state using SessionStatusAutomaton
      try {
        // Use dynamic import to avoid circular dependencies - same pattern as MQTTSubscriber
        const { SessionStatusAutomaton } = await import('../../extension/context/content/services/session-status-automaton');
        whatsappState = SessionStatusAutomaton.getInstance().getWaStatus() || 'UNKNOWN';
        console.log('[Notifier] Current WhatsApp state from SessionStatusAutomaton:', whatsappState);
      } catch (error) {
        console.warn('[Notifier] Error getting WhatsApp state from SessionStatusAutomaton:', error);
        // Fallback to Redux state if SessionStatusAutomaton is not available
        whatsappState = state.WhatsAppReducer?.appState || 'UNKNOWN';
        console.log('[Notifier] Fallback to Redux state:', whatsappState);
      }

      const response = {
        messageExists,
        addedToRedux: !existingMessage,
        whatsappState,
        messageId: message._id
      };

      console.log('[Notifier] insertOnlyIfMissing response:', response);
      callback({
        success: true,
        data: response
      });

    } catch (error) {
      console.error('[Notifier] Error processing insertOnlyIfMissing request:', error);
      callback({
        success: false,
        error: error.message || 'Failed to process insertOnlyIfMissing request'
      });
    }
  }
  return null;
};


// Create message processing handler
export const processMessage = (message: UpdateEventMessageData) => {

  console.log(`[Notifier] Processing message: ${message._messageId} ${message.method} ${message.path}`);

  if (message?.entity?.qrcode) {
    let smallQr = message.entity.qrcode.substring(25, 35);
    console.log('[Notifier] QR code:', smallQr);
  }

  const dbReducerSlice = DBReducerMap.get(message.path);
  if (dbReducerSlice) {
    console.log(`[Notifier] Found DB reducer for path: ${message.path}, dispatching ${message.method} action`);
    switch (message.method) {
      case "create": {
        console.log(`[Notifier] Dispatching addOne for ${message.path}:`, message.entity);
        dispatch(dbReducerSlice.slice.actions.addOne(message.entity));
        break;
      }
      case "patch": {
        const state = getStateDirectly();
        if (message.entity) {
          if (Array.isArray(message.entity)) {
            console.log(`[Notifier] Dispatching updateMany for ${message.path}:`, message.entity.length, 'entities');
            const updatedEntities = message.entity.map((entity) => ({
              id: entity._id,
              changes: entity
            }));
            dispatch(dbReducerSlice.slice.actions.updateMany(updatedEntities));
          } else {
            const entity = dbReducerSlice.selectors.selectById(state, message.entity?._id);
            if (entity) {
              console.log(`[Notifier] Dispatching updateOne for ${message.path}:`, message.entity._id);
              dispatch(
                dbReducerSlice.slice.actions.updateOne({
                  id: message.entity._id,
                  changes: message.entity,
                })
              );
            } else {
              console.log(`[Notifier] Entity not found, dispatching addOne for ${message.path}:`, message.entity._id);
              dispatch(dbReducerSlice.slice.actions.addOne(message.entity));
            }
          }
        }
        break;
      }
      case "remove": {
        console.log(`[Notifier] Dispatching removeOne for ${message.path}:`, message.entity._id);
        dispatch(dbReducerSlice.slice.actions.removeOne(message.entity._id));
        break;
      }
    }
  } else {
    console.warn(`[Notifier] No DB reducer found for path: ${message.path}`);
  }
  return true; // Message processed successfully
};


